// Browser WebSocket wrapper for the Mac agent-sync wire.
// Wave 18 (mini-app side, slice 1). See docs/SYNC_MAC.md.

export interface AtmosConv {
  cid: number;
  name: string;
  kind: string;   // "claude" | "shell" | "agent" | "coding"
  state: string;  // "idle" | "running" | "done" | "failed"
  origin: string; // "atmosphere" | "telegram" | "local"
}

// Agent roster row pushed by the Mac after pair (agent.list.r) and on
// any AgentStore mutation. Mirrors AgentStore's view of an agent: a
// long-lived configurable shell, distinct from a transient conv/run.
export interface AtmosAgent {
  id: string;
  name: string;
  tagline?: string;       // role/preset summary
  iconKey?: string;
  status: "online" | "idle" | "offline" | "busy";
  model?: string;
  tools?: string[];
}

// Per-run lifecycle frame, finer-grained than conv.update. The Mac
// emits these when a chain/agent run is started, makes a step, or
// finishes, so the mini-app's Activity panel can show live cards.
export interface AtmosRunStep { label: string; done?: boolean; active?: boolean }
export interface AtmosRun {
  id: string;            // run id (often = String(cid) for single-conv runs)
  agentId: string;
  agentName: string;
  agentIconKey?: string;
  title: string;
  status: "working" | "done" | "queued" | "failed";
  progress: number;      // 0..1
  startedAt: string;     // unix seconds as string
  steps: AtmosRunStep[];
}

export interface HistoryEntry { role: string; text: string; ts: number }

export type AtmosFrame =
  | { type: "paired" }
  | { type: "conv.list.r"; convs: AtmosConv[] }
  | { type: "conv.update"; cid: number;
      action: "add" | "remove" | "rename" | "state";
      name?: string; kind?: string; state?: string; origin?: string }
  | { type: "msg.delta"; cid: number; text: string }
  | { type: "msg.end"; cid: number; usage?: { ms?: number } }
  | { type: "msg.history.r"; cid: number; messages: HistoryEntry[] }
  | { type: "status"; cid: number; state: string; word?: string }
  | { type: "step"; cid: number; kind: string; text: string; detail?: string }
  | { type: "agent.list.r"; agents: AtmosAgent[] }
  | { type: "run.update"; run: AtmosRun }
  | { type: "tunnel.url.r"; url: string }
  | { type: "auth.err"; reason: string };

export type AtmosLinkState = "down" | "connecting" | "paired" | "error";

export interface AgentCreateCfg {
  name: string;
  role?: string;       // short tagline
  prompt: string;      // system prompt
  model?: string;      // "sonnet" | "opus" | "haiku" — Mac picks default if missing
  tools?: string[];    // e.g. ["code", "web", "shell"]
}

type FrameCb = (frame: AtmosFrame) => void;
type LinkCb = (state: AtmosLinkState) => void;

const BACKOFF_MS = [500, 1000, 2000, 4000, 6000];
const HEARTBEAT_MS = 15_000;
// Dead-link watchdog: if no frame at all (pong, msg.delta, conv.update,
// anything) arrives within this window after a ping was sent, the
// socket is silently dead — force a reconnect. Catches cloudflared and
// phone-NAT half-open sockets where onclose never fires.
const STALE_MS = 22_000;

export class AtmosWS {
  private ws: WebSocket | null = null;
  private url = "";
  private code = "";
  private state: AtmosLinkState = "down";
  private frameSubs = new Set<FrameCb>();
  private linkSubs = new Set<LinkCb>();
  private retryIdx = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFrameAt = 0;
  private wantOpen = false;

  // Optional raw Telegram initData. If set, the WS first-frame is
  // `{initData}` and the Mac side verifies the HMAC instead of (or in
  // addition to) the 6-char pair code. Open-browser users still rely
  // on the pair code only.
  private initData: string | null = null;

  setInitData(raw: string | null): void {
    this.initData = raw && raw.length > 0 ? raw : null;
  }

  connect(wsUrl: string, pairCode: string): void {
    this.url = wsUrl;
    this.code = pairCode;
    this.wantOpen = true;
    this.retryIdx = 0;
    this.openSocket();
  }

  disconnect(): void {
    this.wantOpen = false;
    this.clearTimers();
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.setLink("down");
  }

  // Returns true iff the frame went out. ChatView uses the false
  // return to surface a "Not delivered — link is <state>" system
  // bubble instead of letting the user think their message landed.
  sendMsg(cid: number, text: string): boolean {
    if (this.state !== "paired" || !this.ws) return false;
    try { this.ws.send(JSON.stringify({ type: "msg.send", cid, text })); return true; }
    catch { return false; }
  }

  // New-chat path: send a msg without a cid. Mac WSBridgeSession
  // falls back to its create-agent branch (per SYNC_TG_BOT.md task 3),
  // then broadcasts conv.update so the new conv appears in the
  // drawer and the mini app can re-target follow-up sends.
  sendNew(text: string): boolean {
    if (this.state !== "paired" || !this.ws) return false;
    try { this.ws.send(JSON.stringify({ type: "msg.send", text })); return true; }
    catch { return false; }
  }

  // Agent creation. Per coord/PROTOCOL.md the payload is wrapped:
  //   { type:"agent.create", cfg:{ name, role?, systemPrompt, model?, tools? } }
  // Mac side responds with conv.update action:"add" once spawned.
  createAgent(cfg: AgentCreateCfg): void {
    if (this.state !== "paired" || !this.ws) return;
    const payload = {
      name: cfg.name,
      role: cfg.role,
      systemPrompt: cfg.prompt,
      model: cfg.model,
      tools: cfg.tools,
    };
    try { this.ws.send(JSON.stringify({ type: "agent.create", cfg: payload })); }
    catch { /* reconnect handles */ }
  }

  /** Per coord/PROTOCOL.md: agent.delete fires with the agent id only. */
  deleteAgent(id: string): void {
    if (this.state !== "paired" || !this.ws) return;
    try { this.ws.send(JSON.stringify({ type: "agent.delete", id })); }
    catch { /* reconnect handles */ }
  }

  /** Per coord/PROTOCOL.md: agent.rename fires with id + new name. */
  renameAgent(id: string, name: string): void {
    if (this.state !== "paired" || !this.ws) return;
    try { this.ws.send(JSON.stringify({ type: "agent.rename", id, name })); }
    catch { /* reconnect handles */ }
  }

  /** Per ADR-001: clone an Agent's config into a fresh chat. Mac
   *  replies with agent.list.r + conv.update add carrying the new id. */
  duplicateAgent(id: string): void {
    if (this.state !== "paired" || !this.ws) return;
    try { this.ws.send(JSON.stringify({ v: 0, type: "agent.duplicate", id })); }
    catch { /* reconnect handles */ }
  }

  /** Request message history for a given cid. Mac replies msg.history.r. */
  requestHistory(cid: number, limit?: number): void {
    if (this.state !== "paired" || !this.ws) return;
    const payload: { v: number; type: string; cid: number; limit?: number } = {
      v: 0, type: "msg.history.q", cid
    };
    if (limit !== undefined) payload.limit = limit;
    try { this.ws.send(JSON.stringify(payload)); }
    catch { /* reconnect handles */ }
  }

  /** Request tunnel URL from Mac. Mac replies tunnel.url.r. */
  requestTunnelURL(): void {
    if (this.state !== "paired" || !this.ws) return;
    try { this.ws.send(JSON.stringify({ type: "tunnel.url.q" })); }
    catch { /* reconnect handles */ }
  }

  // Attachment upload — chunks above 1MB are rejected to stay friendly
  // to Mac NWConnection backpressure (which has no streaming receiver
  // yet). Send larger files via the host TG bot's blob channel later.
  // Wire shape mirrors the bot's `attach` pattern: name + mime + b64.
  sendAttach(cid: number, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.state !== "paired" || !this.ws) {
        reject(new Error("not paired"));
        return;
      }
      if (file.size > 1_000_000) {
        reject(new Error("file >1MB; not supported yet"));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result;
          if (typeof result !== "string") {
            reject(new Error("read failed"));
            return;
          }
          // result is "data:<mime>;base64,<payload>"
          const idx = result.indexOf(",");
          const payload = idx >= 0 ? result.slice(idx + 1) : result;
          this.ws!.send(JSON.stringify({
            type: "msg.attach",
            cid,
            name: file.name,
            mime: file.type || "application/octet-stream",
            size: file.size,
            data: payload,
          }));
          resolve();
        } catch (e) { reject(e); }
      };
      reader.onerror = () => reject(reader.error ?? new Error("read error"));
      reader.readAsDataURL(file);
    });
  }

  onFrame(cb: FrameCb): () => void {
    this.frameSubs.add(cb);
    return () => this.frameSubs.delete(cb);
  }

  onLink(cb: LinkCb): () => void {
    this.linkSubs.add(cb);
    return () => this.linkSubs.delete(cb);
  }

  linkState(): AtmosLinkState { return this.state; }

  // --- internals ---

  private openSocket(): void {
    if (!this.url || !this.code) return;
    this.clearTimers();
    this.setLink("connecting");
    let ws: WebSocket;
    try { ws = new WebSocket(this.url); }
    catch { this.setLink("error"); this.scheduleReconnect(); return; }
    this.ws = ws;

    ws.onopen = () => {
      // First frame: prefer signed TG initData when available (Mac will
      // verify HMAC against BOT_TOKEN), fall back to the 6-char pair
      // code for desktop browsers. Mac accepts either.
      const first = this.initData
        ? { initData: this.initData, code: this.code || undefined }
        : { code: this.code };
      try { ws.send(JSON.stringify(first)); } catch { /* ignore */ }
    };

    ws.onmessage = (ev: MessageEvent) => {
      this.lastFrameAt = Date.now();
      let frame: AtmosFrame | null = null;
      try { frame = JSON.parse(typeof ev.data === "string" ? ev.data : "") as AtmosFrame; }
      catch { return; }
      if (!frame || typeof (frame as { type?: unknown }).type !== "string") return;
      if (frame.type === "paired") {
        this.setLink("paired");
        this.retryIdx = 0;
        try { ws.send(JSON.stringify({ type: "conv.list.q" })); } catch { /* ignore */ }
        this.startHeartbeat();
      }
      // A delayed auth.err (Mac restarted, token rotated, code stale)
      // must kick the link off "paired" so the PairWall re-mounts and
      // the user can re-pair instead of typing into a dead socket.
      if (frame.type === "auth.err") {
        this.setLink("error");
        try { ws.close(); } catch { /* ignore */ }
      }
      // Any pong / typed frame counts as a sign of life — feed the
      // stale watchdog so it doesn't kill a busy connection.
      if ((frame as { type: string }).type === "pong") return;
      for (const cb of this.frameSubs) {
        try { cb(frame); } catch { /* swallow */ }
      }
    };

    ws.onerror = () => { this.setLink("error"); };

    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
      if (this.wantOpen) this.scheduleReconnect();
      else this.setLink("down");
    };
  }

  private scheduleReconnect(): void {
    if (!this.wantOpen) return;
    const delay = BACKOFF_MS[Math.min(this.retryIdx, BACKOFF_MS.length - 1)];
    this.retryIdx += 1;
    this.retryTimer = setTimeout(() => this.openSocket(), delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastFrameAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.state === "paired") {
        try { this.ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
        // Watchdog: if no frame at all has arrived in STALE_MS, the
        // socket is half-open. Force-close so onclose fires the normal
        // exp-backoff reconnect path.
        if (this.lastFrameAt && Date.now() - this.lastFrameAt > STALE_MS) {
          try { this.ws.close(); } catch { /* ignore */ }
        }
      }
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    if (this.staleTimer) { clearTimeout(this.staleTimer); this.staleTimer = null; }
  }

  private clearTimers(): void {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.stopHeartbeat();
  }

  private setLink(next: AtmosLinkState): void {
    if (this.state === next) return;
    this.state = next;
    for (const cb of this.linkSubs) {
      try { cb(next); } catch { /* swallow */ }
    }
  }
}

export const atmosWS = new AtmosWS();
